import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, WritableSignal } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";

import {
  FieldErrorConfig,
  FormValidationResult,
  setupFormValidation,
} from "@app/shared/forms/base-form";
import { ContactType } from "@app/shared/models/contact.model";
import { phoneFormatValidator } from "@lib/utils/validations";

export interface ContactModalData {
  title: string;
  saveButtonText?: string;
  cancelButtonText?: string;
  initialData?: ContactModalFormData;
  isEditMode?: boolean;
}

export interface ContactModalFormData {
  contactType: ContactType;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
}

interface ContactFormControls {
  contactType: FormControl<ContactType>;
  contactName: FormControl<string>;
  contactEmail: FormControl<string>;
  contactPhone: FormControl<string>;
}

@Component({
  selector: "app-contact-modal",
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: "./contact-modal.component.html",
  styleUrl: "./contact-modal.component.scss",
})
export class ContactModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  readonly dialogRef = inject(MatDialogRef<ContactModalComponent>);
  readonly data = inject<ContactModalData>(MAT_DIALOG_DATA);

  readonly title = this.data.title;
  readonly saveButtonText =
    this.data.saveButtonText ??
    (this.data.isEditMode ? "Save Changes" : "Save and Continue");
  readonly cancelButtonText = this.data.cancelButtonText ?? "Cancel";
  readonly isEditMode = this.data.isEditMode ?? false;

  private readonly fieldErrorConfigs: FieldErrorConfig[] = [
    {
      controlName: "contactType",
      errorMessages: {
        required: "Please select a contact type.",
      },
    },
    {
      controlName: "contactName",
      errorMessages: {
        required: "Please enter a contact name.",
      },
    },
    {
      controlName: "contactEmail",
      errorMessages: {
        required: "Please enter an email address.",
        email:
          "Please enter a valid email address, like 'john.doe@example.com'.",
      },
    },
    {
      controlName: "contactPhone",
      errorMessages: {
        phoneFormat:
          "The phone number format is invalid. Please use a format like (555) 555-5555.",
      },
    },
  ];

  readonly form: FormGroup<ContactFormControls> = this.fb.group({
    contactType: this.fb.nonNullable.control("" as ContactType, [
      Validators.required,
    ]),
    contactName: this.fb.nonNullable.control("", [Validators.required]),
    contactEmail: this.fb.nonNullable.control("", [
      Validators.required,
      Validators.email,
    ]),
    contactPhone: this.fb.nonNullable.control("", [phoneFormatValidator()]),
  });

  readonly types = Object.entries(ContactType).map(([key, value]) => ({
    key,
    value,
  }));

  private readonly validation: FormValidationResult;

  constructor() {
    this.validation = setupFormValidation({
      form: this.form,
      fieldConfigs: this.fieldErrorConfigs,
    });
  }

  ngOnInit(): void {
    if (this.data.initialData) {
      this.form.patchValue(this.data.initialData);
    }
  }

  errorMessage(controlName: string): WritableSignal<string> {
    const signal = this.validation.getErrorMessage(controlName);
    if (!signal) {
      throw new Error(
        `No error message signal found for control: ${controlName}`,
      );
    }
    return signal;
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onSave(): void {
    if (this.validation.validate()) {
      const formData: ContactModalFormData = this.form.getRawValue();
      this.dialogRef.close(formData);
    }
  }
}
